/* eslint-disable react-hooks/static-components --
   The turntable resolves its renderer from the character registry; that returns
   a stable module-level component, nothing is created during render. */

// The Character Studio: pick a character, pick a pose, move its bones, save.
//
// Extracted from Studio's `/p/:id/characters` route so a game can mount the
// same authoring UI in a dev build with its *own* renderers registered, the
// point being that what you pose is the character players actually meet, not a
// second copy of it that drifts. Studio passes the project roster; a game
// passes the definitions it registered at startup.
//
// ── Why it is built the way it is ───────────────────────────────────────────
//
// A flat column of sliders stops working at about eight bones. A properly
// modelled hand is twenty on its own. So: collapsible groups, degrees with
// typed entry rather than radians on a slider, mirroring, per-bone reset, and
// undo. None of that is ornament: it is the difference between a rig you can
// pose and a rig you give up on.
//
// The lighting presets earn their place for the same reason. A pose judged on
// a bright neutral turntable is a pose that will not read in the game it is
// for; silhouette and raking-torch let you check it the way it will be seen.
//
// Deliberately styled with inline rules over `var(--ss-*, fallback)`. The
// engine's library build emits no CSS, which is why `MapEditorOverlay`'s ~90
// `editor-*` classes have to be duplicated into every host's stylesheet; this
// takes its tokens from the host when they exist and falls back to a dark
// palette when they don't.

import { useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { MeshStandardMaterial, type Group, type Mesh } from 'three';

import type {
  CharacterDefinition,
  CharacterPose,
  CharacterPoseLibrary,
  RigBoneTransform,
} from '../../content/schema/character';
import { getActiveProjectId, getPosePersistence } from '../../editor/persistence/ProjectPersistence';
import { CORE_RIG_BONES } from '../core/NeutralBiped';
import { getCharacterRenderer } from '../rendererRegistry';
import { getRigBones } from '../rigBonesRegistry';
import { makeUniquePoseId, mergePosesById } from '../poseIdentity';
import { mirrorBoneId, mirrorRotation, rigHasSymmetry } from '../rigSymmetry';
import { clipDuration, samplePose, type CharacterClip } from '../animation/characterClip';
import { Viewport } from '../../runtime/viewport/Viewport';
import { useCharacterStudioStore, type CharacterPreviewMode } from './characterStudioStore';
import { TabStrip, type TabDescriptor } from '../../editor/components/Tabs';

const DEG = 180 / Math.PI;
const RAD = Math.PI / 180;
const AXES = ['X', 'Y', 'Z'] as const;

/** A named subset of a rig's bones, for a panel that would otherwise be a wall. */
export interface BoneGroup {
  label: string;
  bones: string[];
}

export type StageLighting = 'studio' | 'torch' | 'silhouette';

const LIGHTING_LABELS: Record<StageLighting, string> = {
  studio: 'Studio',
  torch: 'Torch',
  silhouette: 'Silhouette',
};

const PLAYBACK_RATE_TABS: TabDescriptor[] = [0.25, 0.5, 1].map((rate) => ({ id: String(rate), label: `${rate}×`, title: 'Playback rate' }));

const PREVIEW_MODE_TABS: TabDescriptor[] = [
  { id: 'stage', label: 'Neutral stage' },
  { id: 'scene', label: 'In scene' },
];

/**
 * Neutral matte, for judging form rather than material.
 *
 * Shared and never disposed: one material for every clay render in the session.
 */
const CLAY = new MeshStandardMaterial({ color: '#9c968d', roughness: 0.88, metalness: 0 });

function TurntableCharacter({
  rendererId,
  pose,
  spin,
  clay,
}: {
  rendererId: string;
  pose: CharacterPose | null;
  spin: boolean;
  clay: boolean;
}) {
  const ref = useRef<Group>(null);
  const Renderer = getCharacterRenderer(rendererId);
  useFrame((_, dt) => {
    if (ref.current && spin) ref.current.rotation.y += dt * 0.4;
  });

  // Clay view: swap every material for a neutral matte and put the originals
  // back afterwards. Indispensable for a character who is deliberately
  // near-black: his own materials are designed to give nothing away in a dark
  // room, which is exactly the wrong property when you are trying to see
  // whether a shoulder is the right shape. Originals are stashed per mesh so
  // the swap survives a pose change and always restores.
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const restore: { mesh: Mesh; material: Mesh['material'] }[] = [];
    root.traverse((object) => {
      const mesh = object as Mesh;
      if (!mesh.isMesh) return;
      if (clay) {
        restore.push({ mesh, material: mesh.material });
        mesh.material = CLAY;
      }
    });
    return () => {
      for (const entry of restore) entry.mesh.material = entry.material;
    };
  }, [clay, pose, rendererId]);

  return <group ref={ref}>{Renderer ? <Renderer pose={pose} /> : null}</group>;
}

/** Stage lighting rigs. Each is a judgement about what you need to see. */
function StageLights({ mode }: { mode: StageLighting }) {
  if (mode === 'silhouette') {
    // Outline only. The fastest way to find out whether a pose reads at all.
    return (
      <>
        <ambientLight intensity={0.04} />
        <directionalLight position={[0, 4, -9]} intensity={5.5} color="#aebdd4" />
        <directionalLight position={[-6, 3, -6]} intensity={1.6} color="#8fa2bd" />
      </>
    );
  }
  if (mode === 'torch') {
    // One hard raking source, roughly where a held torch would be. Shows what
    // the pose does in the dark, which is where it will actually be seen.
    return (
      <>
        <ambientLight intensity={0.05} color="#2b3340" />
        <spotLight
          position={[1.4, 2.2, 3.6]}
          angle={0.55}
          penumbra={0.65}
          intensity={26}
          distance={14}
          decay={2}
          color="#ffd9a3"
          castShadow
        />
        <pointLight position={[-3, 1.2, -2]} intensity={0.5} distance={9} color="#2f3a4d" />
      </>
    );
  }
  return (
    <>
      <ambientLight intensity={1.1} />
      <directionalLight position={[4, 8, 6]} intensity={1.8} castShadow />
      <directionalLight position={[-5, 3, -4]} intensity={0.45} color="#cfd8e6" />
    </>
  );
}

export interface CharacterStudioProps {
  /** The characters available to pose. Studio passes `project.characters`. */
  characters: CharacterDefinition[];
  /** Built-in pose libraries, merged with whatever the project has authored. */
  poseLibraries?: CharacterPoseLibrary[];
  /** Bone ids per rig. Falls back to the rig-bones registry when absent. */
  rigBones?: Record<string, string[]>;
  /**
   * Optional grouping per rig id, so a thirty-nine bone skeleton arrives as
   * "Spine / Left arm / Left hand / …" rather than one long column. Bones a
   * group never mentions are collected into a trailing "Other" group, so a
   * partial grouping is safe.
   */
  boneGroups?: Record<string, BoneGroup[]>;
  /**
   * Authored animation per rig id. A rig with clips gains a transport: pick a
   * clip, play it, scrub it. Posing is suspended while one plays, because
   * dragging a bone against a running clip is meaningless and re-rendering a
   * hundred-odd inputs every frame is wasteful.
   */
  clips?: Record<string, CharacterClip[]>;
  /** Panel heading. Omit for none. */
  title?: string;
  /** Rendered as a close control in the header when provided. */
  onClose?: () => void;
  /**
   * Offer the "in scene" preview toggle. Only hosts that render the rig from
   * `useCharacterStudioStore` in their own scene should turn this on.
   */
  allowScenePreview?: boolean;
  style?: CSSProperties;
}

const S = {
  root: {
    display: 'flex',
    height: '100%',
    minHeight: 0,
    color: 'var(--ss-text, #dfe3e8)',
    fontFamily: 'inherit',
    fontSize: 13,
  } satisfies CSSProperties,
  panel: {
    width: 328,
    flexShrink: 0,
    overflowY: 'auto',
    padding: '16px 18px 24px',
    background: 'var(--ss-surface, #14171c)',
    borderRight: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  } satisfies CSSProperties,
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 } satisfies CSSProperties,
  title: { fontSize: 13, fontWeight: 600, letterSpacing: '0.02em', margin: 0 } satisfies CSSProperties,
  section: { display: 'flex', flexDirection: 'column', gap: 8 } satisfies CSSProperties,
  label: {
    fontSize: 10.5,
    textTransform: 'uppercase',
    letterSpacing: '0.13em',
    color: 'var(--ss-text-2, #8b929c)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  } satisfies CSSProperties,
  pillList: { display: 'flex', flexWrap: 'wrap', gap: 6 } satisfies CSSProperties,
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '7px 9px',
    marginTop: 2,
    borderRadius: 8,
    border: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))',
    background: 'var(--ss-panel-strong, #1b1f26)',
    color: 'var(--ss-text, #dfe3e8)',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    textAlign: 'left',
  } satisfies CSSProperties,
  boneRow: { display: 'flex', flexDirection: 'column', gap: 4, padding: '7px 2px 9px 10px' } satisfies CSSProperties,
  boneName: {
    fontSize: 11.5,
    color: 'var(--ss-text, #dfe3e8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
  } satisfies CSSProperties,
  axisRow: { display: 'flex', alignItems: 'center', gap: 6 } satisfies CSSProperties,
  axisTag: { width: 10, fontSize: 10, color: 'var(--ss-text-3, #6d747e)' } satisfies CSSProperties,
  range: { flex: 1, minWidth: 0, accentColor: 'var(--ss-accent, #6444d6)' } satisfies CSSProperties,
  number: {
    width: 54,
    flexShrink: 0,
    background: 'var(--ss-panel-strong, #1b1f26)',
    border: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))',
    borderRadius: 6,
    padding: '3px 5px',
    color: 'var(--ss-text, #dfe3e8)',
    fontSize: 11,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  } satisfies CSSProperties,
  textInput: {
    flex: 1,
    minWidth: 0,
    background: 'var(--ss-panel-strong, #1b1f26)',
    border: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))',
    borderRadius: 8,
    padding: '8px 10px',
    color: 'var(--ss-text, #dfe3e8)',
    fontSize: 13,
  } satisfies CSSProperties,
  saveRow: { display: 'flex', gap: 8 } satisfies CSSProperties,
  status: { fontSize: 12, color: 'var(--ss-accent-strong, #8a5cf2)', margin: '4px 0 0' } satisfies CSSProperties,
  error: { fontSize: 12, color: 'var(--ss-danger, #d0453f)', padding: '2px 0 6px', lineHeight: 1.5, margin: 0 } satisfies CSSProperties,
  hint: { fontSize: 11.5, color: 'var(--ss-text-3, #6d747e)', margin: 0, lineHeight: 1.5 } satisfies CSSProperties,
  stage: { flex: 1, position: 'relative', minWidth: 0 } satisfies CSSProperties,
  stageBar: {
    position: 'absolute',
    left: 14,
    top: 14,
    zIndex: 2,
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    alignItems: 'center',
  } satisfies CSSProperties,
  stageNote: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: 24,
    fontSize: 13,
    lineHeight: 1.6,
    color: 'var(--ss-text-2, #8b929c)',
  } satisfies CSSProperties,
  empty: { padding: 24, fontSize: 13, color: 'var(--ss-text-2, #8b929c)' } satisfies CSSProperties,
};

function pill(active: boolean, disabled = false): CSSProperties {
  return {
    background: active ? 'var(--ss-accent, #6444d6)' : 'var(--ss-panel-strong, #1b1f26)',
    border: `1px solid ${active ? 'var(--ss-accent, #6444d6)' : 'var(--ss-border-soft, rgba(255,255,255,0.08))'}`,
    borderRadius: 7,
    padding: '5px 10px',
    color: active ? 'var(--ss-accent-ink, #081030)' : 'var(--ss-text, #dfe3e8)',
    fontSize: 11.5,
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.4 : 1,
    whiteSpace: 'nowrap',
  };
}

function Pill({
  active = false,
  disabled = false,
  onClick,
  title,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  title?: string;
  children: ReactNode;
}) {
  return (
    <button type="button" style={pill(active, disabled)} disabled={disabled} onClick={onClick} title={title}>
      {children}
    </button>
  );
}

type Overrides = Record<string, RigBoneTransform>;

/** Generic character posing studio, driven entirely by what it is handed. */
export function CharacterStudio({
  characters,
  poseLibraries = [],
  rigBones,
  boneGroups,
  clips,
  title,
  onClose,
  allowScenePreview = false,
  style,
}: CharacterStudioProps) {
  const [characterId, setCharacterId] = useState(characters[0]?.id ?? '');
  const definition = useMemo(
    () => characters.find((c) => c.id === characterId) ?? characters[0],
    [characters, characterId],
  );

  const libraries = useMemo(
    () => poseLibraries.filter((lib) => lib.rigId === definition?.rigId),
    [poseLibraries, definition?.rigId],
  );
  const [authoredPoses, setAuthoredPoses] = useState<CharacterPose[]>([]);

  const poses = useMemo(
    () => [
      ...libraries.flatMap((lib) => lib.poses),
      ...authoredPoses.filter((p) => p.rigId === definition?.rigId),
    ],
    [libraries, authoredPoses, definition?.rigId],
  );

  const [poseId, setPoseId] = useState<string | null>(definition?.defaultPoseId ?? null);
  const [overrides, setOverrides] = useState<Overrides>({});
  const [poseName, setPoseName] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [lighting, setLighting] = useState<StageLighting>('studio');
  const [spin, setSpin] = useState(true);
  const [clay, setClay] = useState(false);
  const [clipId, setClipId] = useState<string | null>(null);
  const [clipTime, setClipTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [clipSpeed, setClipSpeed] = useState(1);
  const [ground, setGround] = useState(true);

  // Undo/redo over the override map. Snapshots rather than diffs: a pose is a
  // few dozen small objects and the clarity is worth more than the bytes.
  const [past, setPast] = useState<Overrides[]>([]);
  const [future, setFuture] = useState<Overrides[]>([]);

  const previewMode = useCharacterStudioStore((s) => s.previewMode);
  const setPreviewMode = useCharacterStudioStore((s) => s.setPreviewMode);
  const setPreviewTarget = useCharacterStudioStore((s) => s.setPreviewTarget);

  const bones = useMemo(() => {
    if (definition) {
      const supplied = rigBones?.[definition.rigId];
      if (supplied && supplied.length > 0) return supplied;
      const registered = getRigBones(definition.rigId) ?? CORE_RIG_BONES[definition.rigId];
      if (registered) return registered;
    }
    const first = poses[0];
    return first ? Object.keys(first.boneTransforms) : [];
  }, [definition, rigBones, poses]);

  /** Grouped for display; anything ungrouped falls into a trailing bucket. */
  const groups = useMemo<BoneGroup[]>(() => {
    const declared = definition ? boneGroups?.[definition.rigId] : undefined;
    if (!declared || declared.length === 0) return [{ label: 'Bones', bones: [...bones] }];
    const known = new Set(declared.flatMap((g) => g.bones));
    const rest = bones.filter((b) => !known.has(b));
    const kept = declared
      .map((g) => ({ label: g.label, bones: g.bones.filter((b) => bones.includes(b)) }))
      .filter((g) => g.bones.length > 0);
    return rest.length > 0 ? [...kept, { label: 'Other', bones: rest }] : kept;
  }, [boneGroups, definition, bones]);

  // ── Animation ───────────────────────────────────────────────────────────
  const rigClips = useMemo(
    () => (definition ? (clips?.[definition.rigId] ?? []) : []),
    [clips, definition],
  );
  const clip = useMemo(() => rigClips.find((c) => c.id === clipId) ?? null, [rigClips, clipId]);
  const duration = clip ? clipDuration(clip) : 0;

  // The transport clock. A rAF loop rather than R3F's frame loop, because the
  // panel is DOM and lives outside the Canvas; the pose it produces is what
  // drives both the turntable and any host scene preview.
  useEffect(() => {
    if (!playing || !clip || duration <= 0) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = ((now - last) / 1000) * clipSpeed;
      last = now;
      setClipTime((prev) => {
        const next = prev + dt;
        if (next < duration) return next;
        if (clip.loop) return next % duration;
        setPlaying(false);
        return duration;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, clip, duration, clipSpeed]);

  // Selecting another character drops the clip with it.
  useEffect(() => {
    setClipId(null);
    setClipTime(0);
    setPlaying(false);
  }, [characterId]);

  const canMirror = useMemo(() => rigHasSymmetry(bones), [bones]);

  const basePose = useMemo(
    () => poses.find((p) => p.id === poseId) ?? poses[0] ?? null,
    [poses, poseId],
  );

  const activePose = useMemo<CharacterPose | null>(() => {
    // A running clip owns the rig outright. Bones it never mentions still fall
    // back to the character's rest attitude in the renderer, so a clip that
    // only moves the arms leaves the rest of him standing.
    if (clip) return samplePose(clip, clipTime);
    if (!basePose) {
      if (Object.keys(overrides).length === 0 || !definition) return null;
      return { id: 'draft', name: 'Draft', rigId: definition.rigId, boneTransforms: { ...overrides } };
    }
    if (Object.keys(overrides).length === 0) return basePose;
    return { ...basePose, boneTransforms: { ...basePose.boneTransforms, ...overrides } };
  }, [clip, clipTime, basePose, overrides, definition]);

  const hasUnsavedPose = Object.keys(overrides).length > 0;
  const [poseLoadError, setPoseLoadError] = useState(false);
  // Only matters for a rig with no static pose-library entries (poses stays
  // [] until the authored-pose fetch below resolves) - without this, "None
  // yet, pose from rest and save" rendered for the length of that fetch even
  // when the rig actually has saved poses on the way.
  const [posesLoading, setPosesLoading] = useState(true);

  /** Every mutation of the override map goes through here, so undo is total. */
  const commit = useCallback((next: (prev: Overrides) => Overrides) => {
    setOverrides((prev) => {
      const value = next(prev);
      if (value === prev) return prev;
      setPast((p) => [...p.slice(-49), prev]);
      setFuture([]);
      return value;
    });
  }, []);

  const undo = useCallback(() => {
    setPast((p) => {
      if (p.length === 0) return p;
      const previous = p[p.length - 1]!;
      setOverrides((current) => {
        setFuture((f) => [current, ...f.slice(0, 49)]);
        return previous;
      });
      return p.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setFuture((f) => {
      if (f.length === 0) return f;
      const next = f[0]!;
      setOverrides((current) => {
        setPast((p) => [...p.slice(-49), current]);
        return next;
      });
      return f.slice(1);
    });
  }, []);

  // Warn before a tab close / reload drops in-progress posing.
  useEffect(() => {
    if (!hasUnsavedPose) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = 'You have an unsaved pose.';
      return event.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedPose]);

  const confirmDiscardIfDirty = useCallback((): boolean => {
    if (!hasUnsavedPose) return true;
    return window.confirm('You have an unsaved pose. Discard those changes?');
  }, [hasUnsavedPose]);

  // Load authored poses. `getPosePersistence` and `getActiveProjectId` both
  // throw when the host has not wired them, so the synchronous part needs the
  // same guard as the request, otherwise mounting the studio before startup
  // wiring takes the whole panel down instead of degrading to "no saved poses".
  useEffect(() => {
    let cancelled = false;
    setPoseLoadError(false);
    setPosesLoading(true);
    try {
      getPosePersistence()
        .loadPoses(getActiveProjectId())
        .then((res) => {
          if (!cancelled) setAuthoredPoses(res.poses);
        })
        .catch(() => {
          if (!cancelled) setPoseLoadError(true);
        })
        .finally(() => {
          if (!cancelled) setPosesLoading(false);
        });
    } catch {
      setPoseLoadError(true);
      setPosesLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the selection valid when the roster arrives or changes underneath us.
  useEffect(() => {
    if (characters.length === 0) return;
    if (!characters.some((c) => c.id === characterId)) setCharacterId(characters[0]!.id);
  }, [characters, characterId]);

  useEffect(() => {
    setPoseId(definition?.defaultPoseId ?? null);
    setOverrides({});
    setPast([]);
    setFuture([]);
  }, [characterId, definition?.defaultPoseId]);

  // Publish what is being posed so a host can draw it in its own scene.
  useEffect(() => {
    setPreviewTarget({
      characterId: definition?.id ?? null,
      rendererId: definition?.rendererId ?? null,
      activePose,
    });
  }, [definition?.id, definition?.rendererId, activePose, setPreviewTarget]);

  const rotationOf = useCallback(
    (bone: string): [number, number, number] =>
      (overrides[bone]?.rotation ?? basePose?.boneTransforms[bone]?.rotation ?? [0, 0, 0]) as [number, number, number],
    [overrides, basePose],
  );

  const setBoneAxis = useCallback(
    (bone: string, axis: 0 | 1 | 2, radians: number) => {
      commit((prev) => {
        const current = (prev[bone]?.rotation ?? basePose?.boneTransforms[bone]?.rotation ?? [0, 0, 0]) as [number, number, number];
        const next: [number, number, number] = [current[0], current[1], current[2]];
        next[axis] = radians;
        return { ...prev, [bone]: { ...prev[bone], rotation: next } };
      });
    },
    [basePose, commit],
  );

  const resetBone = useCallback(
    (bone: string) => {
      commit((prev) => {
        if (!(bone in prev)) return prev;
        const next = { ...prev };
        delete next[bone];
        return next;
      });
    },
    [commit],
  );

  const resetAll = useCallback(() => {
    commit((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, [commit]);

  /** Copy every posed bone onto its opposite number, reflected. Only bones the
   * user actually posed (present in `prev`) act as a mirror source - iterating
   * every rig bone regardless would also treat each twin's mere existence as a
   * source, overwriting a just-authored pose with the mirror of its (usually
   * still-default) twin once the loop reached that twin in turn. */
  const mirrorPose = useCallback(() => {
    commit((prev) => {
      const next: Overrides = { ...prev };
      for (const bone of bones) {
        if (!(bone in prev)) continue;
        const twin = mirrorBoneId(bone);
        if (!twin || !bones.includes(twin)) continue;
        const source = (prev[bone]?.rotation ?? basePose?.boneTransforms[bone]?.rotation) as
          | [number, number, number]
          | undefined;
        if (!source) continue;
        next[twin] = { ...next[twin], rotation: mirrorRotation(source) };
      }
      return next;
    });
  }, [bones, basePose, commit]);

  const savePose = useCallback(async () => {
    if (!activePose || !definition || !poseName.trim()) return;
    const existingIds = poses.map((p) => p.id);
    const pose: CharacterPose = {
      id: makeUniquePoseId(definition.rigId, poseName, existingIds),
      name: poseName.trim(),
      rigId: definition.rigId,
      description: `Authored pose for ${definition.name}.`,
      tags: ['authored'],
      boneTransforms: { ...activePose.boneTransforms },
    };
    setSaving(true);
    try {
      setStatus('Saving…');
      const res = await getPosePersistence().savePoses(getActiveProjectId(), [pose]);
      // `savePoses` resolves with the poses it was handed, not the project's
      // whole set: merge rather than replace or every previously authored
      // pose disappears from the list until reload.
      setAuthoredPoses((prev) => mergePosesById(prev, res.poses.length > 0 ? res.poses : [pose]));
      setPoseName('');
      setPoseId(pose.id);
      setOverrides({});
      setPast([]);
      setFuture([]);
      setStatus(`Saved "${pose.name}"`);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
    setTimeout(() => setStatus(null), 3000);
  }, [activePose, definition, poseName, poses]);

  if (!definition) {
    return <div style={{ ...S.empty, ...style }}>This project has no characters.</div>;
  }

  const posedCount = Object.keys(overrides).length;

  return (
    <div style={{ ...S.root, ...style }}>
      <aside style={S.panel}>
        {(title || onClose) && (
          <div style={S.header}>
            {title ? <h2 style={S.title}>{title}</h2> : <span />}
            {onClose && <Pill onClick={onClose}>Close</Pill>}
          </div>
        )}

        <section style={S.section}>
          <span style={S.label}>Character</span>
          <TabStrip
            listLabel="Character"
            variant="compact"
            scope="character"
            tabs={characters.map((c): TabDescriptor => ({ id: c.id, label: c.name }))}
            activeId={definition.id}
            onSelect={(id) => {
              if (id === characterId || !confirmDiscardIfDirty()) return;
              setCharacterId(id);
            }}
          />
        </section>

        <section style={S.section}>
          <span style={S.label}>
            <span>Pose</span>
            <span style={{ letterSpacing: 0, textTransform: 'none', fontSize: 11 }}>
              {posedCount > 0 ? `${posedCount} bone${posedCount === 1 ? '' : 's'} changed` : ''}
            </span>
          </span>
          {poseLoadError && <p style={S.error}>Saved poses failed to load. New poses can still be saved.</p>}
          {posesLoading && poses.length === 0 ? (
            <p style={S.hint}>Loading poses…</p>
          ) : poses.length === 0 ? (
            <p style={S.hint}>None yet, pose from rest and save.</p>
          ) : (
            <TabStrip
              listLabel="Pose"
              variant="compact"
              scope="pose"
              tabs={poses.map((p): TabDescriptor => ({ id: p.id, label: p.name }))}
              activeId={basePose?.id ?? ''}
              onSelect={(id) => {
                if (id === basePose?.id || !confirmDiscardIfDirty()) return;
                setPoseId(id);
                setOverrides({});
                setPast([]);
                setFuture([]);
              }}
            />
          )}
          <div style={S.pillList}>
            <Pill onClick={undo} disabled={past.length === 0} title="Undo the last bone change">
              ↶ Undo
            </Pill>
            <Pill onClick={redo} disabled={future.length === 0} title="Redo">
              ↷ Redo
            </Pill>
            <Pill onClick={resetAll} disabled={posedCount === 0} title="Drop all unsaved changes">
              Reset all
            </Pill>
            {canMirror && (
              <Pill onClick={mirrorPose} title="Copy each posed bone onto its opposite side, reflected">
                ⇄ Mirror
              </Pill>
            )}
          </div>
        </section>

        {rigClips.length > 0 && (
          <section style={S.section}>
            <span style={S.label}>
              <span>Animation</span>
              {clip && (
                <span style={{ letterSpacing: 0, textTransform: 'none', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
                  {clipTime.toFixed(2)}s / {duration.toFixed(2)}s
                </span>
              )}
            </span>
            <TabStrip
              listLabel="Animation clip"
              variant="compact"
              scope="clip"
              tabs={rigClips.map((c): TabDescriptor => ({ id: c.id, label: c.name, title: c.description }))}
              activeId={clipId ?? ''}
              onSelect={(id) => {
                if (id === clipId) {
                  setClipId(null);
                  setPlaying(false);
                  return;
                }
                if (!confirmDiscardIfDirty()) return;
                setOverrides({});
                setClipId(id);
                setClipTime(0);
                setPlaying(true);
              }}
            />
            {clip && (
              <>
                <div style={S.pillList}>
                  <Pill active={playing} onClick={() => setPlaying((v) => !v)}>
                    {playing ? '⏸ Pause' : '▶ Play'}
                  </Pill>
                  <Pill onClick={() => { setClipTime(0); }} title="Back to the first frame">
                    ⏮ Start
                  </Pill>
                  <TabStrip
                    listLabel="Playback rate"
                    variant="compact"
                    scope="rate"
                    tabs={PLAYBACK_RATE_TABS}
                    activeId={String(clipSpeed)}
                    onSelect={(id) => setClipSpeed(Number(id))}
                  />
                </div>
                <input
                  type="range"
                  style={{ ...S.range, width: '100%' }}
                  min={0}
                  max={Math.max(duration, 0.001)}
                  step={0.01}
                  value={clipTime}
                  onChange={(e) => {
                    setPlaying(false);
                    setClipTime(Number(e.target.value));
                  }}
                />
                {clip.description && <p style={S.hint}>{clip.description}</p>}
              </>
            )}
          </section>
        )}

        <section style={S.section}>
          <span style={S.label}>
            <span>Bones</span>
            <span style={{ letterSpacing: 0, textTransform: 'none', fontSize: 11 }}>{bones.length}</span>
          </span>
          {clip && (
            <p style={S.hint}>
              Posing is suspended while “{clip.name}” is loaded. Deselect the clip to edit bones.
            </p>
          )}
          {!clip && groups.map((group) => {
            const isCollapsed = collapsed[group.label] ?? group.bones.length > 12;
            const changedHere = group.bones.filter((b) => b in overrides).length;
            return (
              <div key={group.label}>
                <button
                  type="button"
                  style={S.groupHeader}
                  onClick={() => setCollapsed((c) => ({ ...c, [group.label]: !isCollapsed }))}
                >
                  <span style={{ width: 9, color: 'var(--ss-text-3, #6d747e)' }}>{isCollapsed ? '▸' : '▾'}</span>
                  <span style={{ flex: 1 }}>{group.label}</span>
                  <span style={{ fontSize: 10.5, color: changedHere > 0 ? 'var(--ss-accent-strong, #8a5cf2)' : 'var(--ss-text-3, #6d747e)' }}>
                    {changedHere > 0 ? `${changedHere}/${group.bones.length}` : group.bones.length}
                  </span>
                </button>
                {!isCollapsed &&
                  group.bones.map((bone) => {
                    const rotation = rotationOf(bone);
                    const changed = bone in overrides;
                    return (
                      <div key={bone} style={S.boneRow}>
                        <span style={S.boneName}>
                          <span style={{ color: changed ? 'var(--ss-accent-strong, #8a5cf2)' : undefined }}>{bone}</span>
                          {changed && (
                            <button
                              type="button"
                              onClick={() => resetBone(bone)}
                              title={`Reset ${bone}`}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--ss-text-3, #6d747e)',
                                cursor: 'pointer',
                                fontSize: 11,
                                padding: 0,
                              }}
                            >
                              reset
                            </button>
                          )}
                        </span>
                        {AXES.map((axis, i) => (
                          <div key={axis} style={S.axisRow}>
                            <span style={S.axisTag}>{axis}</span>
                            <input
                              type="range"
                              style={S.range}
                              min={-180}
                              max={180}
                              step={0.5}
                              value={Math.round(rotation[i]! * DEG * 10) / 10}
                              onChange={(e) => setBoneAxis(bone, i as 0 | 1 | 2, Number(e.target.value) * RAD)}
                            />
                            <input
                              type="number"
                              style={S.number}
                              min={-180}
                              max={180}
                              step={0.5}
                              value={Math.round(rotation[i]! * DEG * 10) / 10}
                              onChange={(e) => {
                                const deg = Number(e.target.value);
                                if (Number.isFinite(deg)) setBoneAxis(bone, i as 0 | 1 | 2, deg * RAD);
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </section>

        <section style={S.section}>
          <span style={S.label}>Save pose</span>
          <div style={S.saveRow}>
            <input
              style={S.textInput}
              placeholder="Pose name…"
              value={poseName}
              onChange={(e) => setPoseName(e.target.value)}
            />
            <Pill active disabled={!poseName.trim() || saving} onClick={() => void savePose()}>
              {saving ? 'Saving…' : 'Save'}
            </Pill>
          </div>
          {status ? <p style={S.status}>{status}</p> : null}
        </section>
      </aside>

      <div style={S.stage}>
        <div style={S.stageBar}>
          {allowScenePreview && (
            <TabStrip
              listLabel="Preview stage"
              variant="compact"
              scope="preview"
              tabs={PREVIEW_MODE_TABS}
              activeId={previewMode}
              onSelect={(id) => setPreviewMode(id as CharacterPreviewMode)}
            />
          )}
          {previewMode === 'stage' && (
            <>
              <TabStrip
                listLabel="Lighting"
                variant="compact"
                scope="lighting"
                tabs={(Object.keys(LIGHTING_LABELS) as StageLighting[]).map(
                  (mode): TabDescriptor => ({
                    id: mode,
                    label: LIGHTING_LABELS[mode],
                    title:
                      mode === 'silhouette'
                        ? 'Backlit only: does the pose read as an outline?'
                        : mode === 'torch'
                          ? 'One raking source, as a held torch would light it'
                          : 'Even neutral light for judging detail',
                  }),
                )}
                activeId={lighting}
                onSelect={(id) => setLighting(id as StageLighting)}
              />
              <Pill active={spin} onClick={() => setSpin((v) => !v)} title="Turntable rotation">
                {spin ? '⏸ Spin' : '▶ Spin'}
              </Pill>
              <Pill
                active={clay}
                onClick={() => setClay((v) => !v)}
                title="Neutral matte over every material: the only way to read the form of a near-black character"
              >
                Clay
              </Pill>
              <Pill active={ground} onClick={() => setGround((v) => !v)} title="Ground plane">
                Ground
              </Pill>
            </>
          )}
        </div>

        {previewMode === 'scene' && allowScenePreview ? (
          <div style={S.stageNote}>
            <span>Previewing {definition.name} in the live scene.</span>
            <span style={{ fontSize: 11.5, color: 'var(--ss-text-3, #6d747e)' }}>
              Bone changes apply there as you drag.
            </span>
          </div>
        ) : (
          <Viewport shadows>
            <PerspectiveCamera makeDefault position={[2.6, 1.7, 3.4]} fov={42} />
            <color attach="background" args={[lighting === 'studio' ? '#0f1318' : '#07090c']} />
            <StageLights mode={lighting} />
            {ground && (
              <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
                <circleGeometry args={[5, 48]} />
                <meshStandardMaterial color="#181b1f" roughness={0.92} />
              </mesh>
            )}
            <TurntableCharacter rendererId={definition.rendererId} pose={activePose} spin={spin} clay={clay} />
            <OrbitControls enablePan={false} minDistance={0.6} maxDistance={8} target={[0, 0.9, 0]} />
          </Viewport>
        )}
      </div>
    </div>
  );
}
